import { Link, useLocation, Outlet } from "react-router-dom"
import { useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  LayoutDashboard, Building2, Users, Wallet, Wrench,
  FileText, Store, Settings, ChevronLeft, ChevronRight,
  MoreHorizontal, LogOut,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useUIStore } from "@/stores/uiStore"
import { useAuthStore } from "@/stores/authStore"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { getInitials } from "@/lib/utils"
import { TrialBanner } from "@/components/shared/TrialBanner"
import { GoogleAnalytics } from "@/components/shared/GoogleAnalytics"
import { ClipboardList } from "lucide-react"
import { APP_NAME, GA_ID, MARKETPLACE_ENABLED } from "@/lib/appConfig"
import { useScheduleNotifications } from "@/hooks/useScheduleNotifications"
import { api } from "@/lib/api"

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: "Home", path: "/dashboard" },
  { icon: Building2, label: "Properties", path: "/properties" },
  { icon: Users, label: "Tenants", path: "/tenants" },
  { icon: ClipboardList, label: "Leases", path: "/leases" },
  { icon: Wallet, label: "Finances", path: "/finances" },
  { icon: Wrench, label: "Maintenance", path: "/maintenance" },
  { icon: FileText, label: "Documents", path: "/documents" },
  ...(MARKETPLACE_ENABLED ? [{ icon: Store, label: "Marketplace", path: "/marketplace" }] : []),
  { icon: Settings, label: "Settings", path: "/settings" },
]

const BOTTOM_NAV = [
  { icon: LayoutDashboard, label: "Home", path: "/dashboard" },
  { icon: Building2, label: "Properties", path: "/properties" },
  { icon: Users, label: "Tenants", path: "/tenants" },
  { icon: Wallet, label: "Finances", path: "/finances" },
  { icon: MoreHorizontal, label: "More", path: "/more" },
]

export default function AppLayout() {
  const location = useLocation()
  const { sidebarOpen, toggleSidebar } = useUIStore()
  const { user, logout, setUser } = useAuthStore()
  useScheduleNotifications()

  // Re-fetch user profile on mount to refresh presigned avatar URL
  useEffect(() => {
    api.get("/api/users").then((res) => {
      if (res.data?.success) setUser(res.data.data)
    }).catch(() => {})
  }, [])

  const isActive = (path: string) => {
    if (path === "/dashboard") return location.pathname === "/dashboard"
    return location.pathname.startsWith(path)
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden md:flex flex-col m-4 rounded-3xl border border-border/50 bg-card/60 backdrop-blur-2xl shadow-soft transition-all duration-300 z-10",
          sidebarOpen ? "w-64" : "w-20"
        )}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-border">
          {sidebarOpen ? (
            <div className="flex items-center gap-2 flex-1">
              <img src="/app-icon.png" className="h-7 w-7 object-contain" alt={APP_NAME} />
              <span className="font-bold text-base text-foreground">{APP_NAME.toUpperCase()}</span>
            </div>
          ) : (
            <img src="/app-icon.png" className="h-7 w-7 object-contain mx-auto" alt={APP_NAME} />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className={cn("h-8 w-8 shrink-0", !sidebarOpen && "mx-auto")}
          >
            {sidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 overflow-y-auto">
          <div className="space-y-0.5 px-2">
            {NAV_ITEMS.map(({ icon: Icon, label, path }) => (
              <Link
                key={path}
                to={path}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium transition-all duration-200 active:scale-[0.97]",
                  isActive(path)
                    ? "bg-primary text-primary-foreground shadow-glow"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                )}
              >
                <Icon className="h-[18px] w-[18px] shrink-0" />
                {sidebarOpen && <span>{label}</span>}
              </Link>
            ))}
          </div>
        </nav>

        {/* User section */}
        <div className={cn("p-3 border-t border-border", !sidebarOpen && "flex flex-col items-center gap-2")}>
          {sidebarOpen ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2 px-2 py-1">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Account</span>
              </div>
              <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-accent cursor-pointer">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={user?.avatar_url || undefined} />
                  <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                    {getInitials(user?.full_name || user?.email || "U")}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.full_name || "Account"}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={logout}>
                  <LogOut className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.avatar_url || undefined} />
                <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                  {getInitials(user?.full_name || user?.email || "U")}
                </AvatarFallback>
              </Avatar>
            </>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <GoogleAnalytics id={GA_ID} />
        <TrialBanner />

        <div className="flex-1 overflow-y-auto relative pb-24 md:pb-4 rounded-t-3xl md:rounded-t-none">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="h-full"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mobile bottom navigation */}
        <nav className="md:hidden fixed bottom-6 left-4 right-4 z-50 flex border border-border/50 bg-card/80 backdrop-blur-2xl rounded-3xl shadow-glow-lg h-16 px-2">
          {BOTTOM_NAV.map(({ icon: Icon, label, path }) => (
            <Link
              key={path}
              to={path}
              className={cn(
                "flex-1 flex flex-col items-center justify-center gap-1 transition-all duration-200 active:scale-95 rounded-2xl relative",
                isActive(path)
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {isActive(path) && (
                <motion.div layoutId="mobile-nav-indicator" className="absolute -top-2 w-1.5 h-1.5 bg-primary rounded-full shadow-glow" />
              )}
              <motion.div animate={{ scale: isActive(path) ? 1.15 : 1 }}>
                <Icon className="h-[22px] w-[22px]" />
              </motion.div>
              <span className="text-[10px] font-semibold">{label}</span>
            </Link>
          ))}
        </nav>
      </main>
    </div>
  )
}
